import { useEffect, useId, useRef } from "react";

type ErrorDialogProps = {
  open: boolean;
  title?: string;
  message: string;
  onRetry: () => void;
  onCancel: () => void;
  retryLabel?: string;
  cancelLabel?: string;
};

export const ErrorDialog = ({
  open,
  title = "Something went wrong",
  message,
  onRetry,
  onCancel,
  retryLabel = "Try again",
  cancelLabel = "Cancel",
}: ErrorDialogProps) => {
  const titleId = useId();
  const messageId = useId();
  const retryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    retryRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">{title}</h2>
        <p id={messageId} className="mt-2 text-sm text-slate-600">{message}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={retryRef}
            type="button"
            onClick={onRetry}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
          >
            {retryLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
